import { Injectable } from '@angular/core';
import { HttpRequest, HttpResponse, HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { Observable, of, delay, firstValueFrom } from 'rxjs';
import { map, filter, switchMap, catchError } from 'rxjs/operators';
import { AppContext, IService } from '../Shared/common';
import { Global } from '../Shared/global';

@Injectable()
export class CommonDataService implements IService {
  //headers: HttpHeaders;
  //options: {};

  constructor(private _http: HttpClient, private appctx: AppContext) {
    // Don't initialize headers here - wait until we have context
  }

  /**
   * ✅ Refresh headers before EVERY request
   * This ensures OrgId, UnitId, and UserId are always current
   */
  private refreshHeaders(): HttpHeaders {
    const orgId = this.getOrgId();
    const unitId = this.getUnitId();
    const userId = this.getUserId();

    Global.Log('[CommonDataService] Refreshing headers - OrgId:', orgId, 'UnitId:', unitId, 'UserId:', userId);

    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'q=0.8;application/json;q=0.9',
      'OrgId': orgId,
      'UnitId': unitId,
      'UserId': userId
    });
  }

  /**
   * Safely get OrgId from appctx
   */
  private getOrgId(): string {
    try {
      if (this.appctx?.CurrentOrg?.OrgId) {
        return this.appctx.CurrentOrg.OrgId.toString();
      }
    } catch (e) {
      Global.LogWarning('[CommonDataService] Error getting OrgId:', e);
    }
    return '';
  }

  /**
   * Safely get UnitId from appctx
   */
  private getUnitId(): string {
    try {
      if (this.appctx?.CurrentOrg?.OrgUnits) {
        const selectedUnit = this.appctx.CurrentOrg.OrgUnits.find(x => x.IsSelected);
        if (selectedUnit?.UnitId) {
          Global.Log('[CommonDataService] Selected unit found:', selectedUnit.UnitId);
          return selectedUnit.UnitId.toString();
        }
      }
    } catch (e) {
      Global.LogWarning('[CommonDataService] Error getting UnitId:', e);
    }
    return '';
  }

  /**
   * Safely get UserId from appctx
   */
  private getUserId(): string {
    try {
      if (this.appctx?.CurrentUser?.ID) {
        return this.appctx.CurrentUser.ID.toString();
      }
    } catch (e) {
      Global.LogWarning('[CommonDataService] Error getting UserId:', e);
    }
    return '';
  }

  get(url: string): Observable<any> {
    return this._http.get(url).pipe(
      map((response: Response) => response),
      catchError(<T>(error: any, result?: T) => {
        Global.LogError('[CommonDataService] GET error:', error);
        return typeof (result as T);
      })
    );
  }

  /**
   * ✅ Refresh headers on EVERY getData call
   */
  getDataV1(url: string): Promise<any> {
    Global.Log('[CommonDataService] getData URL:', url);

    // ✅ Refresh headers BEFORE making the request
    const freshHeaders = this.refreshHeaders();
    const options = { headers: freshHeaders };

    return this._http
      .get(url, options)
      .toPromise()
      .then(res => {
        Global.Log("HTTP response received", res);
        return this.extractData(<any>res);
      })
      .catch(err => {
        Global.Log("HTTP error", err);
        this.handleError(err);
        throw err;
      });
  }

  /**
   * ✅ Refresh headers on EVERY getData call
   */
  getData(url: string): Promise<any> {
    Global.Log('[CommonDataService] getData URL:', url);
    const freshHeaders = this.refreshHeaders();
    const options = { headers: freshHeaders };

    return firstValueFrom(
      this._http.get(url, options)
    )
      .then(res => {
        Global.Log("HTTP response received", res);
        return this.extractData(<any>res);
      })
      .catch(err => {
        Global.Log("HTTP error", err);
        this.handleError(err);
        throw err;
      });
  }

  /**
   * ✅ Refresh headers on EVERY getDownloadData call
   */
  getDownloadData(url: string): Promise<any> {
    Global.Log('[CommonDataService] getDownloadData URL:', url);

    const freshHeaders = this.refreshHeaders();
    const options = { headers: freshHeaders };

    return this._http
      .get(url, options)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  /**
   * ✅ Refresh headers on EVERY DocumentsDownload call
   */
  DocumentsDownload(url: string): Promise<any> {
    Global.Log('[CommonDataService] DocumentsDownload URL:', url);

    const freshHeaders = this.refreshHeaders();

    return this._http.get(url, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
      headers: freshHeaders
    })
      .toPromise()
      .then(this.extractBlobData)
      .catch(this.handleError);
  }

  /**
   * ✅ Refresh headers on EVERY post call
   */
  post(url: string, model: any): Observable<any> {
    Global.Log('[CommonDataService] POST URL:', url, 'Headers:', this.refreshHeaders());

    const freshHeaders = this.refreshHeaders();
    const options = { headers: freshHeaders };

    return this._http.post(url, model, options)
      .pipe(
        map((response: Response) => response),
        catchError(<T>(error: any, result?: T) => {
          Global.LogError('[CommonDataService] POST error:', error);
          return typeof (result as T);
        }));
  }

  /**
   * ✅ Refresh headers on EVERY put call
   */
  put(url: string, id: number, model: any): Observable<any> {
    Global.Log('[CommonDataService] PUT URL:', url + id);

    const freshHeaders = this.refreshHeaders();
    const options = { headers: freshHeaders };

    return this._http.put(url + id, model, options)
      .pipe(
        map((response: Response) => response),
        catchError(<T>(error: any, result?: T) => {
          Global.LogError('[CommonDataService] PUT error:', error);
          return typeof (result as T);
        }));
  }

  /**
   * ✅ Refresh headers on EVERY delete call
   */
  delete(url: string, id: number): Observable<any> {
    Global.Log('[CommonDataService] DELETE URL:', url + id);

    const freshHeaders = this.refreshHeaders();
    const options = { headers: freshHeaders };

    return this._http.delete(url + id, options)
      .pipe(
        map((response: Response) => response),
        catchError(<T>(error: any, result?: T) => {
          Global.LogError('[CommonDataService] DELETE error:', error);
          return typeof (result as T);
        }));
  }

  private extractData(res: Response) {
    let body = res;
    return body || {};
  }

  private extractBlobData(res: any) {
    let body = res;
    return body || {};
  }

  private handleError(error: any): Promise<any> {
    Global.LogError('[CommonDataService] An error occurred:', error);
    return Promise.reject(error.message || error);
  }
}
